import * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'

import {
  ChevronDownIcon,
  IconButton,
  Sidebar,
  Tooltip,
  useSidebar,
} from '@buffer-mono/popcorn'

import styles from './ExpandableSidebarListItem.module.css'

/**
 * ExpandableSidebarListItem - A compound component for creating expandable/collapsible Sidebar items
 *
 * This component provides a flexible pattern for Sidebar items that can expand to show nested content.
 * It supports both controlled and uncontrolled state, following the same patterns as Popcorn components
 * like DropdownMenu.
 *
 * Key Features:
 * - Compound component pattern with Trigger, Toggle, and Content sub-components
 * - Controlled/uncontrolled state via expanded/defaultExpanded props
 * - To be used inside the Sidebar Popcorn component
 * - Auto-hides content when Sidebar is collapsed
 *
 * The Toggle is a sibling of the row's `Sidebar.Button`, never its `suffix`:
 * `Sidebar.Button` renders `suffix` inside its own root, so a Toggle passed there
 * would nest a button inside the row's link.
 *
 * @example
 * // Basic uncontrolled usage
 * <ExpandableSidebarListItem>
 *   <ExpandableSidebarListItem.Trigger>
 *     <Sidebar.Button as={NavLink} to={url}>
 *       Channel Name
 *     </Sidebar.Button>
 *     <ExpandableSidebarListItem.Toggle label="Channel Name submenu" />
 *   </ExpandableSidebarListItem.Trigger>
 *   <ExpandableSidebarListItem.Content>
 *     <Sidebar.Button indent={1}>Sub Item 1</Sidebar.Button>
 *     <Sidebar.Button indent={1}>Sub Item 2</Sidebar.Button>
 *   </ExpandableSidebarListItem.Content>
 * </ExpandableSidebarListItem>
 *
 * @example
 * // Controlled usage with parent coordination
 * function ParentComponent() {
 *   const [expandedId, setExpandedId] = useState<string | null>(null)
 *
 *   return (
 *     <>
 *       {items.map(item => (
 *         <ExpandableSidebarListItem
 *           key={item.id}
 *           expanded={expandedId === item.id}
 *           onExpandedChange={(expanded) => setExpandedId(expanded ? item.id : null)}
 *         >
 *           <ExpandableSidebarListItem.Trigger>
 *             <Sidebar.Button as={NavLink} to={item.url}>
 *               {item.name}
 *             </Sidebar.Button>
 *             <ExpandableSidebarListItem.Toggle label={`${item.name} submenu`} />
 *           </ExpandableSidebarListItem.Trigger>
 *           <ExpandableSidebarListItem.Content>
 *             {item.children}
 *           </ExpandableSidebarListItem.Content>
 *         </ExpandableSidebarListItem>
 *       ))}
 *     </>
 *   )
 * }
 */

type ExpandableSidebarListItemContextValue = {
  isExpanded: boolean
  toggleExpanded: () => void
  /** Ties the Toggle's `aria-controls` to the Content it expands. */
  contentId: string
}

const ExpandableSidebarListItemContext =
  React.createContext<ExpandableSidebarListItemContextValue | null>(null)

const useExpandableSidebarListItem =
  (): ExpandableSidebarListItemContextValue => {
    const context = React.useContext(ExpandableSidebarListItemContext)
    if (!context) {
      throw new Error(
        'useExpandableSidebarListItem must be used within ExpandableSidebarListItem',
      )
    }
    return context
  }

type ExpandableSidebarListItemProps = {
  /**
   * The children of the expandable list item (typically Trigger and Content)
   */
  children: React.ReactNode
  /**
   * Additional class name
   */
  className?: string
  /**
   * Controlled expanded state
   */
  expanded?: boolean
  /**
   * Default expanded state when uncontrolled
   * @default false
   */
  defaultExpanded?: boolean
  /**
   * Callback when expanded state changes
   */
  onExpandedChange?: (expanded: boolean) => void
  /**
   * Prevent transitions (used during navigation for instant expand/collapse)
   * @default false
   */
  preventTransitions?: boolean
} & React.HTMLAttributes<HTMLDivElement>

/**
 * Root component for an expandable sidebar list item
 * Manages the expanded/collapsed state
 */
const ExpandableSidebarListItem = ({
  children,
  className,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandedChange,
  preventTransitions = false,
  ...props
}: ExpandableSidebarListItemProps): JSX.Element => {
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded)
  const contentId = React.useId()

  const isControlled = controlledExpanded !== undefined
  const isExpanded = isControlled ? controlledExpanded : uncontrolledExpanded

  const toggleExpanded = React.useCallback(() => {
    const newValue = !isExpanded
    if (!isControlled) {
      setUncontrolledExpanded(newValue)
    }
    onExpandedChange?.(newValue)
  }, [isExpanded, isControlled, onExpandedChange])

  const contextValue = React.useMemo(
    () => ({
      isExpanded,
      toggleExpanded,
      contentId,
    }),
    [isExpanded, toggleExpanded, contentId],
  )

  return (
    <ExpandableSidebarListItemContext.Provider value={contextValue}>
      <div
        className={className}
        data-prevent-transitions={preventTransitions}
        {...props}
      >
        {children}
      </div>
    </ExpandableSidebarListItemContext.Provider>
  )
}

ExpandableSidebarListItem.displayName = 'ExpandableSidebarListItem'

type TriggerProps = {
  /**
   * The row content - a Sidebar.Button followed by the Toggle as its sibling
   */
  children: React.ReactNode
  /**
   * Additional class name
   */
  className?: string
  /**
   * Optional mouse leave handler
   */
  onMouseLeave?: () => void
}

/**
 * Trigger component for the expandable list item.
 * The semantic <li> belongs to the outer wrapper under <ul> (DraggableChannelItem
 * or the plain <li> in the non-draggable path); this wrapper only supplies the
 * Popcorn sidebar item styling and hover/data attributes used by the suffix UI.
 */
const Trigger = ({
  children,
  className,
  onMouseLeave,
}: TriggerProps): JSX.Element => {
  return (
    <Sidebar.Item className={className} onMouseLeave={onMouseLeave}>
      {children}
    </Sidebar.Item>
  )
}

Trigger.displayName = 'ExpandableSidebarListItem.Trigger'

type ToggleProps = {
  /**
   * Accessible name for the toggle, also shown as its tooltip. Must stay stable
   * across expand and collapse: `aria-expanded` carries the state, so a name
   * that changes with it would be announced twice and would break find-by-name
   * in screen reader rotors.
   */
  label: string
  /**
   * Additional class name
   */
  className?: string
}

/**
 * Toggle button for expanding/collapsing the item.
 * Render as a sibling of the row's Sidebar.Button, never as its suffix.
 */
const Toggle = ({ label, className }: ToggleProps): JSX.Element => {
  const { isExpanded, toggleExpanded, contentId } =
    useExpandableSidebarListItem()
  const { state: sidebarState } = useSidebar()
  const isCollapsed = sidebarState === 'collapsed'

  const handleClick = React.useCallback((): void => {
    if (isCollapsed) return
    toggleExpanded()
  }, [isCollapsed, toggleExpanded])

  return (
    // The tooltip repeats `label` so its visible text is part of the accessible
    // name (WCAG 2.5.3). The rotated chevron shows the state to sighted users
    // and `aria-expanded` reports it to assistive tech, so `label` stays stable.
    <Tooltip content={label}>
      <IconButton
        type="button"
        variant="tertiary"
        size="small"
        onClick={handleClick}
        className={className}
        aria-expanded={isExpanded}
        // Content is unmounted while the sidebar is collapsed, so referencing it
        // would leave a dangling id.
        aria-controls={isCollapsed ? undefined : contentId}
        label={label}
      >
        <ChevronDownIcon
          className={isExpanded ? styles.chevronExpanded : styles.chevron}
        />
      </IconButton>
    </Tooltip>
  )
}

Toggle.displayName = 'ExpandableSidebarListItem.Toggle'

type ContentProps = {
  /**
   * The content to show when expanded (typically Sidebar.Button items with indent)
   */
  children: React.ReactNode
  /**
   * Additional class name
   */
  className?: string
}

/**
 * Content component for the expandable list item
 * Shows/hides based on expanded state with smooth animation
 */
const Content = ({ children, className }: ContentProps): JSX.Element | null => {
  const { isExpanded, contentId } = useExpandableSidebarListItem()
  const { state: sidebarState } = useSidebar()

  // Don't render at all when sidebar is collapsed
  if (sidebarState === 'collapsed') {
    return null
  }

  return (
    <div
      id={contentId}
      className={`${styles.contentWrapper} ${className ?? ''}`}
      data-expanded={isExpanded}
    >
      <div className={styles.contentInner}>{children}</div>
    </div>
  )
}

Content.displayName = 'ExpandableSidebarListItem.Content'

// Compound component export
const ExpandableSidebarListItemObject = Object.assign(
  ExpandableSidebarListItem,
  {
    Trigger,
    Toggle,
    Content,
  },
)

export { ExpandableSidebarListItemObject as ExpandableSidebarListItem }
export type {
  ExpandableSidebarListItemProps,
  TriggerProps as ExpandableSidebarListItemTriggerProps,
  ToggleProps as ExpandableSidebarListItemToggleProps,
  ContentProps as ExpandableSidebarListItemContentProps,
}
